import { useCallback, useEffect, useState } from "react";
import { currencyMinorUnits } from "@v0y4ge/jrni-ui";
import { tv2Call } from "../../tickets/v2/tickets-v2-api";
import { currentLocale } from "../../../lib/i18n";

export interface EventTicket {
  id: string; name: string; desc: string; price: number;
  sold: number; capacity: number; currency: string;
  /** A retired tier sells no more, but the seats it sold stay sold — they count toward revenue. */
  retired: boolean;
}

/* One event's ticket tiers, read from the event's own ticketing (CC, 2026-10-04):
   GET /v1/admin/events/:id/ticketing, which needs events.read only, so every role that can open the
   event can read it. This replaces getEventTickets(), a shim that always returned [] — the drawer's
   Tickets tab rendered blank, its count never showed and the Overview revenue tile was always $0.
   sold is the tier's live availability.sold, capacity its seats. Retired tiers stay in the data (their
   sold seats are revenue); the Tickets tab lists only the active ones.
   priceMinor is in the event currency's minor units: the exponent comes from the design system's
   currencyMinorUnits (JPY 0, KWD 3, most 2), never a fixed /100. */
function toTicket(t: Record<string, any>, currency: string): EventTicket {
  return {
    id: String(t.id ?? ""),
    name: String(t.name ?? ""),
    desc: "",
    price: Number(t.priceMinor ?? 0) / 10 ** currencyMinorUnits(currency),
    sold: Number(t.availability?.sold ?? 0),
    capacity: Number(t.seats ?? 0),
    currency,
    retired: t.status === "retired",
  };
}

/** A major-unit amount in `currency`, formatted for the current locale with that currency's own decimals. */
export function formatTicketMoney(amount: number, currency: string): string {
  const digits = currencyMinorUnits(currency);
  try {
    return new Intl.NumberFormat(currentLocale(), { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount);
  } catch {
    return `${amount.toFixed(digits)} ${currency}`;
  }
}

export type EventTicketsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; tickets: EventTicket[] };

/** The event's tiers with loading / error / ready, and a retry for the error state. */
export function useEventTicketing(eventId: string | null | undefined): EventTicketsState & { retry: () => void } {
  const [state, setState] = useState<EventTicketsState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!eventId) { setState({ status: "ready", tickets: [] }); return; }
    let live = true;
    setState({ status: "loading" });
    tv2Call("GET", `/v1/admin/events/${encodeURIComponent(eventId)}/ticketing`)
      .then((r: any) => {
        if (!live) return;
        const tk = r?.ticketing ?? {};
        const currency = String(tk.currency ?? "USD");
        const tiers: Record<string, any>[] = Array.isArray(tk.tiers) ? tk.tiers : [];
        setState({ status: "ready", tickets: tiers.map((x) => toTicket(x, currency)) });
      })
      .catch((e: any) => { if (live) setState({ status: "error", message: String(e?.message || e) }); });
    return () => { live = false; };
  }, [eventId, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
